import React, { useEffect, useState } from 'react';
import { Theme, Typography, Button, useTheme, alpha } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MinusIcon from '@mui/icons-material/Remove';
import PlusIcon from '@mui/icons-material/Add';
import useMediaQuery from '@mui/material/useMediaQuery';
import classNames from 'classnames';
import { RiDeleteBinLine } from 'react-icons/ri';
import { useTranslation } from 'services/localization/useTranslation';

interface OwnProps {
	variantName: string;
	variantId: string;
	unitAmount: number;
	onDeleteRow: (id: string) => void;
	onAddVariant: (id: string) => void;
	onRemoveVariant: (id: string) => void;
	addedByVariantCard: boolean;
	variantAvailability: number | undefined;
	rowNumber: number;
	isSetWithVariants: boolean;
}

type Props = OwnProps & WithStyles<typeof styles>;

const SelectedVariantRow = (props: Props) => {
	const theme = useTheme();
	const { classes, unitAmount, addedByVariantCard, variantAvailability, isSetWithVariants } = props;
	const [showBackgroundHighlight, setShowBackgroundHighlight] = useState(false);
	const overBooked = variantAvailability ? variantAvailability < 0 && !isSetWithVariants : false;
	const { t } = useTranslation();

	useEffect(() => {
		if (addedByVariantCard && (unitAmount > 1 || props.rowNumber > 0)) {
			setShowBackgroundHighlight(true);
		}
		const timer = setTimeout(() => {
			if (addedByVariantCard) {
				setShowBackgroundHighlight(false);
			}
		}, 300);
		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [unitAmount]);

	return (
		<div>
			<Grid
				className={classNames(
					overBooked ? classes.selectedVariantRowBooked : classes.selectedVariantRow,
					showBackgroundHighlight ? classes.selectedVariantRowWithHighlight : null,
				)}
				container
				spacing={0}
				key={props.variantId}
				alignItems="center"
				justifyContent="space-between"
			>
				<Grid>
					<div className={classes.productSelection}>
						<Button
							className={classes.addBtn}
							variant="text"
							onClick={() => props.onRemoveVariant(props.variantId)}
						>
							<MinusIcon color="primary" fontSize="large" />
						</Button>
						<div className={classes.productCountContainer}>
							<Typography variant="body1" color="inherit" className={classes.addUnit}>
								{props.unitAmount}
							</Typography>
						</div>
						<Button
							className={classes.addBtn}
							variant="text"
							onClick={() => props.onAddVariant(props.variantId)}
						>
							<PlusIcon color="primary" fontSize="large" />
						</Button>
					</div>
				</Grid>
				<Grid item xs className={classes.textBlock}>
					<Typography variant={useMediaQuery(theme.breakpoints.up('sm')) ? 'body1' : 'body2'}>
						{props.variantName}
					</Typography>
					{overBooked && (
						<Typography variant="caption" color="error">
							{t('newRental.overbooking', 'You are about to overbook')}
						</Typography>
					)}
				</Grid>
				<Grid>
					<IconButton onClick={(e) => props.onDeleteRow(props.variantId)} size="large">
						<RiDeleteBinLine className={classes.bin} />
					</IconButton>
				</Grid>
			</Grid>
		</div>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		selectedVariantRow: {
			padding: '16px 0px',
		},
		selectedVariantRowBooked: {
			padding: '16px 0px 0px 0px',
		},
		selectedVariantRowWithHighlight: {
			backgroundColor: alpha(theme.palette.primary.main, 0.1),
		},
		overbooked: {
			paddingLeft: '10px',
		},
		addButton: {
			padding: 8,
			flex: '0 0 auto',
			margin: 8,
			minWidth: 0,
		},
		productSelection: {
			display: 'flex',
			justifyContent: 'flex-end',
			alignItems: 'center',
			marginRight: 16,
		},
		addBtn: {
			padding: 8,
			flex: '0 0 auto',
			minWidth: 0,
		},
		productCountContainer: {
			flex: '0 0 auto',
			width: 36,
			height: 38,
			[theme.breakpoints.up('sm')]: {
				width: 48,
				height: 50,
			},
			borderRadius: '4px',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			backgroundColor: theme.palette.primary.main,
			border: 'none',
			color: theme.palette.primary.contrastText,
		},
		addUnit: {
			fontSize: '16px',
		},
		textBlock: {
			textAlign: 'left',
			lineHeight: 0.5,
		},
		bin: {
			color: theme.palette.colors.tundra.main,
		},
	});

export default withStyles(styles)(SelectedVariantRow);
